// The five looks of the staff sign-in page. Settings → Branding → Login page
// design stores one of their keys (login_template); AdminLoginPage keeps the
// sign-in logic and hands each layout the brand, the page-root style tokens
// and the finished form panel (mode tabs, alert, forms, security tag). A
// layout only decides where the logo, headline, form and footer go and what
// surrounds them — the wording, images and colours are the brand's own and
// are the same in all five. Styles: AdminLoginPage.css (passbook, shared
// form parts) and AdminLoginTemplates.css (the other four).

import { medallionStops } from '../utils/loginBranding';

// A guilloché ring: the interlocking engraved lines found on currency notes,
// share certificates, and passbook covers — built from N copies of one
// ellipse, each rotated a fraction further around the centre.
function GuillocheRing({ rx, ry, count, strokeWidth = 0.55, opacity = 0.4 }) {
    const step = 180 / count;
    return (
        <>
            {Array.from({ length: count }).map((_, i) => (
                <ellipse
                    key={i}
                    cx="0"
                    cy="0"
                    rx={rx}
                    ry={ry}
                    transform={`rotate(${i * step})`}
                    fill="none"
                    stroke="url(#loginGoldLine)"
                    strokeWidth={strokeWidth}
                    opacity={opacity}
                />
            ))}
        </>
    );
}

export function GuillocheMedallion({ accent }) {
    const [stopA, stopB] = medallionStops(accent);
    return (
        <svg className="login-medallion" viewBox="-260 -260 520 520" aria-hidden="true" focusable="false">
            <defs>
                <linearGradient id="loginGoldLine" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor={stopA} />
                    <stop offset="100%" stopColor={stopB} />
                </linearGradient>
            </defs>
            <g className="login-medallion-spin-a">
                <GuillocheRing rx={232} ry={82} count={40} strokeWidth={0.6} opacity={0.4} />
            </g>
            <g className="login-medallion-spin-b">
                <GuillocheRing rx={176} ry={62} count={32} strokeWidth={0.5} opacity={0.32} />
            </g>
            <g className="login-medallion-spin-a">
                <GuillocheRing rx={120} ry={44} count={24} strokeWidth={0.45} opacity={0.28} />
            </g>
            <circle cx="0" cy="0" r="60" fill="none" stroke="url(#loginGoldLine)" strokeWidth="0.8" opacity="0.4" />
            <circle cx="0" cy="0" r="248" fill="none" stroke="url(#loginGoldLine)" strokeWidth="0.8" opacity="0.3" />
        </svg>
    );
}

// The brand's mark; held back until the server has said which one it is, so
// a replaced logo never flashes from a stale cache.
const Mark = ({ brand }) => (brand.ready ? <img src={brand.logoSrc} alt={brand.brandName} /> : null);

const LegalNotice = ({ brand, className = '' }) => (
    <p className={`admin-legal-notice ${className}`.trim()}>
        © {new Date().getFullYear()} {brand.legalName}{brand.footerNote ? ` — ${brand.footerNote}` : ''}
    </p>
);

// ── Passbook ────────────────────────────────────────────────────────────────
// The original: a warm card with the logo in a coin seal over its top edge
// and the engraved medallion behind it.
export function PassbookLayout({ brand, style, children }) {
    return (
        <div className="admin-login-page login-template-passbook" style={style}>
            <div className="login-stage">
                <GuillocheMedallion accent={brand.accentColor} />

                <div className="admin-login-card">
                    <div className="certificate-seal">
                        <Mark brand={brand} />
                    </div>

                    <div className="admin-login-header">
                        <h1 className="admin-page-title">{brand.welcomeTitle}</h1>
                        <span className="certificate-suffix">{brand.legalName}</span>
                        {brand.tagline && <p className="admin-page-subtitle">{brand.tagline}</p>}
                    </div>

                    {children}
                </div>

                <LegalNotice brand={brand} />
            </div>
        </div>
    );
}

// ── Split panel ─────────────────────────────────────────────────────────────
// A tall dark panel carries the brand and its tagline as the headline, like
// the printed face of a cheque leaf, with the company name set in the
// machine-readable strip along its foot; the form sits on white beside it.
export function SplitLayout({ brand, style, children }) {
    return (
        <div className="admin-login-page login-template-split" style={style}>
            <div className="split-shell">
                <aside className="split-panel">
                    <div className="split-panel-top">
                        <div className="split-mark"><Mark brand={brand} /></div>
                        <span className="split-brand">{brand.brandName}</span>
                    </div>
                    {brand.tagline && (
                        <div className="split-panel-copy">
                            <h2 className="split-headline">{brand.tagline}</h2>
                        </div>
                    )}
                    <div className="split-panel-foot">
                        <span className="split-micr">{brand.legalName}</span>
                    </div>
                </aside>

                <main className="split-main">
                    <div className="split-form">
                        <div className="admin-login-header split-header">
                            <span className="certificate-eyebrow">{brand.brandName} Admin Portal</span>
                            <h1 className="admin-page-title">{brand.welcomeTitle}</h1>
                        </div>

                        {children}

                        <LegalNotice brand={brand} />
                    </div>
                </main>
            </div>
        </div>
    );
}

// ── Ledger ──────────────────────────────────────────────────────────────────
// No card: the whole page is a ruled register leaf with the accent-coloured
// margin line of an accounts book, the logo and names at its head, and the
// form written straight onto the rules.
export function MinimalLayout({ brand, style, children }) {
    return (
        <div className="admin-login-page login-template-minimal" style={style}>
            <header className="ledger-head">
                <div className="ledger-mark"><Mark brand={brand} /></div>
                <div className="ledger-names">
                    <strong>{brand.brandName}</strong>
                    <span>{brand.legalName}</span>
                </div>
            </header>

            <main className="ledger-main">
                <div className="ledger-form">
                    <div className="admin-login-header ledger-header">
                        <h1 className="admin-page-title">{brand.welcomeTitle}</h1>
                        {brand.tagline && <p className="admin-page-subtitle">{brand.tagline}</p>}
                    </div>

                    {children}
                </div>
            </main>

            <LegalNotice brand={brand} className="ledger-foot" />
        </div>
    );
}

// ── Midnight ────────────────────────────────────────────────────────────────
// A dark page under one soft spotlight in the accent colour, a solid card
// with the logo in a tile at its head.
export function MidnightLayout({ brand, style, children }) {
    return (
        <div className="admin-login-page login-template-midnight" style={style}>
            <div className="midnight-spot" aria-hidden="true" />
            <div className="login-stage">
                <div className="admin-login-card midnight-card">
                    <div className="midnight-top">
                        <div className="midnight-mark"><Mark brand={brand} /></div>
                        <div className="midnight-names">
                            <strong>{brand.brandName}</strong>
                            <span>{brand.legalName}</span>
                        </div>
                    </div>

                    <div className="admin-login-header midnight-header">
                        <h1 className="admin-page-title">{brand.welcomeTitle}</h1>
                        {brand.tagline && <p className="admin-page-subtitle">{brand.tagline}</p>}
                    </div>

                    {children}
                </div>

                <LegalNotice brand={brand} />
            </div>
        </div>
    );
}

// ── Signboard ───────────────────────────────────────────────────────────────
// A wide band in the accent colour across the top, like the fascia above a
// branch counter, carrying the brand and the welcome line; the form card
// hangs over the band's lower edge.
export function HorizonLayout({ brand, style, children }) {
    return (
        <div className="admin-login-page login-template-horizon" style={style}>
            <header className="signboard">
                <div className="signboard-inner">
                    <div className="signboard-brand">
                        <div className="signboard-mark"><Mark brand={brand} /></div>
                        <div className="signboard-names">
                            <strong>{brand.brandName}</strong>
                            <span>{brand.legalName}</span>
                        </div>
                    </div>
                    <h1 className="signboard-title">{brand.welcomeTitle}</h1>
                    {brand.tagline && <p className="signboard-tagline">{brand.tagline}</p>}
                </div>
            </header>

            <main className="signboard-main">
                <div className="admin-login-card signboard-card">
                    {children}
                </div>
                <LegalNotice brand={brand} />
            </main>
        </div>
    );
}

export const LOGIN_LAYOUTS = Object.freeze({
    passbook: PassbookLayout,
    split: SplitLayout,
    minimal: MinimalLayout,
    midnight: MidnightLayout,
    horizon: HorizonLayout
});
